import { makeAutoObservable, runInAction } from "mobx";
import { RouteType } from "types/RouteType";
import { RouterStoreConfig } from "../RouterStore";
import makeRoute from "../makeRoute";
import RootStore from "../../RootStore";

/**
 * Manages route registration, matching, and lookup operations.
 * Follows Single Responsibility Principle by focusing only on route management.
 */
export class RouteManager {
    // Observable state
    routesList: RouteType[] = [];
    routes: { [key: string]: RouteType } = {};

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * Registers routes from a list of route configurations
     * @param routesList - Array of route configurations to register
     * @param config - Router configuration settings
     * @param rootStore - Reference to root store for route lifecycle methods
     */
    async setRoutes(
        routesList: RouteType[],
        config: RouterStoreConfig,
        rootStore: RootStore
    ): Promise<void> {
        const routes = await this.routesFromList(routesList);

        runInAction(() => {
            this.routesList = routesList;
            this.routes = routes;
        });
    }

    /**
     * Converts route list to route object for fast lookup
     * @param routes - Array of route configurations
     * @returns Object with route keys mapped to route instances
     */
    private async routesFromList(routes: RouteType[]): Promise<{ [key: string]: RouteType }> {
        const routesObject: { [key: string]: RouteType } = {};

        for (const route of routes) {
            const routeInstance = makeRoute(route);
            // Cast the Route instance back to RouteType for compatibility
            routesObject[routeInstance.key] = routeInstance as unknown as RouteType;
        }

        return routesObject;
    }

    /**
     * Finds a route by its key/name
     * @param routeKey - The route key to search for
     * @returns Route instance or null if not found
     */
    findRouteByKey(routeKey: string): RouteType | null {
        return this.routes[routeKey] || null;
    }

    /**
     * Finds a route that matches the given path
     * @param path - URL path to match against routes
     * @returns Route instance or null if no match found
     */
    findRouteByPath(path: string): RouteType | null {
        for (const route of this.routesList) {
            if (!route?.name) continue;
            const routeInstance = this.routes[route.name.replace(/ /g, '')];
            if (this.pathMatchesRoute(path, routeInstance)) {
                return routeInstance;
            }
        }
        return null;
    }

    /**
     * Checks if a path matches a specific route pattern
     * @param path - URL path to test
     * @param route - Route instance to test against
     * @returns True if path matches route pattern
     */
    private pathMatchesRoute(path: string, route: RouteType): boolean {
        // Simple path matching logic - can be enhanced for complex patterns
        if (!route?.path) return false;
        const routePath = route.path.replace(/:[^/]+/g, '[^/]+');
        const pathRegex = new RegExp(`^${routePath}$`);
        return pathRegex.test(path);
    }

    /**
     * Gets all routes that require authentication
     * @returns Array of routes requiring authentication
     */
    getAuthenticatedRoutes(): RouteType[] {
        return this.routesList.filter(route => {
            if (!route?.name) return false;
            return this.routes[route.name.replace(/ /g, '')].requiresAuth;
        });
    }

    /**
     * Gets all routes that require admin privileges
     * @returns Array of routes requiring admin access
     */
    getAdminRoutes(): RouteType[] {
        return this.routesList.filter(route => {
            if (!route?.name) return false;
            return this.routes[route.name.replace(/ /g, '')].requiresAdmin;
        });
    }

    /**
     * Gets routes organized by permission path for navigation menus
     * @returns Array of route sections with permissions
     */
    getRoutesByPermissionPath(): {
        sectionName: string;
        sections: { name: string; fullPath: string }[];
    }[] {
        const sections: { [key: string]: { name: string; fullPath: string }[] } = {};

        for (const route of this.routesList) {
            if (!route?.name) continue;
            const routeInstance = this.routes[route.name.replace(/ /g, '')];
            const permissionPath = routeInstance.permissionPath || routeInstance.path;
            const sectionName = permissionPath?.split('/')[1] || 'root';

            if (!sections[sectionName]) {
                sections[sectionName] = [];
            }

            sections[sectionName].push({
                name: routeInstance.title || routeInstance.name || '',
                fullPath: routeInstance.path || ''
            });
        }

        return Object.entries(sections).map(([sectionName, routes]) => ({
            sectionName,
            sections: routes
        }));
    }
}
